import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, Clapperboard } from "lucide-react";
import { MaskedLine, EASE } from "@/lib/motion";
import { WHATSAPP_URL } from "@/lib/filters";

const CHIPS = ["NO INSTALL", "NO SIGN-UP", "RUNS ON YOUR PHONE"];

export default function Hero() {
    const sectionRef = useRef(null);
    const { scrollY } = useScroll();
    const bgY = useTransform(scrollY, [0, 900], [0, 170]);
    const cardY = useTransform(scrollY, [0, 900], [0, -60]);
    const cardRotate = useTransform(scrollY, [0, 900], [2.5, -2.5]);

    useEffect(() => {
        const v = document.getElementById("hero-bg-video");
        if (v) v.play().catch(() => {});
    }, []);

    return (
        <section ref={sectionRef} className="relative flex min-h-[100svh] items-end overflow-hidden">
            <motion.div style={{ y: bgY, scale: 1.08 }} className="absolute inset-0 will-change-transform">
                <video
                    id="hero-bg-video"
                    className="h-full w-full object-cover"
                    src="/samples/city.webm"
                    poster="https://images.unsplash.com/photo-1574717024757-c1ec4d86ae82?auto=format&fit=crop&w=1600&q=80"
                    autoPlay
                    muted
                    loop
                    playsInline
                />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/55 to-obsidian/25" />
            <div className="absolute inset-0 bg-gradient-to-r from-obsidian/85 via-obsidian/30 to-transparent" />

            <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-24 pt-32 sm:px-6 sm:pb-28 lg:px-8">
                <motion.p
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
                    className="mb-5 font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-amberx sm:text-xs"
                >
                    Deoeditz — Pocket Video Studio
                </motion.p>

                <h1 className="font-display text-[13.5vw] font-black leading-[0.93] tracking-tight sm:text-7xl lg:text-8xl">
                    <MaskedLine delay={0.25}>EDIT VIDEOS.</MaskedLine>
                    <MaskedLine delay={0.38}>RIGHT IN</MaskedLine>
                    <MaskedLine delay={0.51}>
                        YOUR <span className="text-amberx">POCKET.</span>
                    </MaskedLine>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.75, ease: EASE }}
                    className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
                >
                    Upload a clip, tap a filter, share the result. Deoeditz runs entirely in your
                    browser — the full edit happens on your phone, in seconds.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.9, ease: EASE }}
                    className="mt-8 flex flex-wrap items-center gap-3"
                >
                    <Link
                        to="/editor"
                        data-testid="hero-open-editor-button"
                        className="inline-flex h-14 items-center gap-2.5 rounded-full bg-amberx px-8 font-display text-base font-bold text-obsidian shadow-[0_0_40px_rgba(255,159,28,0.35)] transition-transform hover:scale-[1.03] active:scale-95"
                    >
                        <Clapperboard className="h-5 w-5" />
                        Open the Editor
                    </Link>
                    <a
                        href={WHATSAPP_URL}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="hero-whatsapp-button"
                        className="inline-flex h-14 items-center gap-2.5 rounded-full border border-white/25 px-8 font-display text-base font-bold text-slate-100 backdrop-blur-sm transition-colors hover:border-amberx hover:text-amberx"
                    >
                        <MessageCircle className="h-5 w-5" />
                        WhatsApp us
                    </a>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 1.1 }}
                    className="mt-10 flex flex-wrap gap-2"
                >
                    {CHIPS.map((c) => (
                        <span
                            key={c}
                            className="rounded-full border border-white/15 px-3.5 py-1.5 font-mono text-[10px] font-bold tracking-[0.2em] text-slate-300"
                        >
                            {c}
                        </span>
                    ))}
                </motion.div>
            </div>

            <motion.div
                style={{ y: cardY, rotate: cardRotate }}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 1.05, ease: EASE }}
                className="absolute bottom-20 right-6 z-10 hidden w-64 overflow-hidden rounded-2xl border border-white/15 bg-obsidian/60 shadow-2xl backdrop-blur-md md:right-10 lg:block"
            >
                <div className="relative aspect-[9/13]">
                    <video
                        className="h-full w-full object-cover"
                        style={{ filter: "contrast(130%) saturate(140%) hue-rotate(-15deg) sepia(20%)" }}
                        src="/samples/waves.webm"
                        autoPlay
                        muted
                        loop
                        playsInline
                    />
                    <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-obsidian/70 px-2.5 py-1 font-mono text-[9px] font-bold tracking-[0.18em] text-amberx backdrop-blur-sm">
                        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emberx" />
                        LIVE PREVIEW
                    </span>
                    <span className="absolute right-3 top-3 rounded-full bg-obsidian/70 px-2.5 py-1 font-mono text-[9px] font-bold tracking-[0.14em] text-slate-200 backdrop-blur-sm">
                        TEAL &amp; ORANGE
                    </span>
                    <div className="absolute inset-x-0 bottom-0 h-1 bg-obsidian/60">
                        <div className="timebar-fill h-full bg-amberx" />
                    </div>
                </div>
            </motion.div>
        </section>
    );
}
